import type { PoolFootprint } from './ask-helix/contracts/governed-tool-output-envelope';
import {
  DECISION_LEDGER_REALIZATION_COMMITMENT_SCHEMA_VERSION,
  type DecisionLedgerRealizationCommitmentDto,
  type DecisionLedgerRealizationCommitmentMetricDirectionDto,
  type DecisionLedgerRealizationCommitmentProposalMetadataDto,
  type DecisionLedgerVerificationMetricSensitivityDto,
} from './decision-ledger';
import {
  validateCustomerOverlapLeverExtension,
  type CustomerOverlapLeverExtension,
} from './ma-synergy-customer-overlap';
import { validateSynergyUnderwritingBands } from './ma-synergy-band-validation';

export const MA_SYNERGY_COMMITMENT_SCHEMA_VERSION = 'ma_synergy_commitment_v1' as const;
export const MA_SYNERGY_COMMITMENT_V1_SCHEMA_VERSION = MA_SYNERGY_COMMITMENT_SCHEMA_VERSION;
export const MA_SYNERGY_COMMITMENT_V2_SCHEMA_VERSION = 'ma_synergy_commitment_v2' as const;
export const MA_SYNERGY_COMMITMENT_SCHEMA_VERSIONS = [
  MA_SYNERGY_COMMITMENT_V1_SCHEMA_VERSION,
  MA_SYNERGY_COMMITMENT_V2_SCHEMA_VERSION,
] as const;
export const MA_SYNERGY_UNDERWRITING_CONTEXT_KIND = 'ma_fusion:synergy_underwriting' as const;

export const SYNERGY_CERTAINTY_CLASSES = [
  'c0_observed',
  'c1_committed_lever',
  'c2_modelled_upside',
  'c3_hypothesis',
  'blocked',
] as const;

export const GOVERNED_SYNERGY_CERTAINTY_CLASSES = ['c0_observed', 'c1_committed_lever'] as const;

export const SYNERGY_VALUE_TYPES = [
  'workforce_cost_takeout',
  'vendor_spend',
  'facility_footprint',
  'stranded_sga',
  'revenue_cross_sell',
  'working_capital',
  'risk_avoidance',
] as const;

export type SynergyCommitmentSchemaVersion =
  (typeof MA_SYNERGY_COMMITMENT_SCHEMA_VERSIONS)[number];
export type SynergyCertaintyClass = (typeof SYNERGY_CERTAINTY_CLASSES)[number];
export type GovernedSynergyCertaintyClass = (typeof GOVERNED_SYNERGY_CERTAINTY_CLASSES)[number];
export type SynergyValueType = (typeof SYNERGY_VALUE_TYPES)[number];
export type SynergySourceMode = 'pre_deal' | 'clean_room' | 'day_1_plus';
export type SynergyProjectionApprovedBand = 'conservative' | 'base' | 'stretch';

export type SynergyUnderwritingBands = Readonly<{
  conservativeRunRateUsd: number;
  baseRunRateUsd: number;
  stretchRunRateUsd: number;
  oneTimeCostUsd: number;
  bandBasis: string;
}>;

export type RealizationEvidenceDefinition = Readonly<{
  metricKey: string;
  unit: string;
  direction: DecisionLedgerRealizationCommitmentMetricDirectionDto;
  observableSource: string;
  sourceContextKind: string;
  reconciliationRule: string;
  proofArtifactKind: string;
}>;

export type SynergyLeverCommitment = Readonly<{
  schemaVersion: SynergyCommitmentSchemaVersion;
  dealId: string;
  scenarioVersionId: string;
  synergyLeverId: string;
  title: string;
  valueType: SynergyValueType;
  sourceMode: SynergySourceMode;
  certaintyClass: SynergyCertaintyClass;
  bands: SynergyUnderwritingBands;
  owner: Readonly<{
    ownerUserId: string | null;
    ownerLabel: string;
    workstreamId: string;
  }>;
  sizingEvidenceRefIds: readonly string[];
  poolFootprint: PoolFootprint;
  overlapDimensionKeys: readonly string[];
  realizationEvidence: RealizationEvidenceDefinition;
  realizationCurve: readonly Readonly<{
    asOf: string;
    plannedValue: number;
    timingProfile: string;
  }>[];
  blockingGapCodes: readonly string[];
  governedCommitmentDecisionId: string | null;
  customerOverlap?: CustomerOverlapLeverExtension;
}>;

export type SynergyLeverCommitmentValidationResult =
  | Readonly<{ ok: true; value: SynergyLeverCommitment }>
  | Readonly<{ ok: false; errors: readonly string[] }>;

export type SynergyProjectionMetricBindingInput = Readonly<{
  label: string;
  definition: string;
  scope: string;
  sourceArtifactId: string;
  sourceMetricId: string;
  sensitivity: DecisionLedgerVerificationMetricSensitivityDto;
  baselineValue: number;
  baselineAsOf: string;
  warningThresholdRatio?: number | null;
  driftThresholdRatio?: number | null;
  amendmentThresholdRatio?: number | null;
}>;

export type SynergyProjectionValueBindingInput = Readonly<{
  formulaId: string;
  formulaVersion: string;
  formulaRef: string;
  outputMetric: string;
  asOf: string;
  sourceArtifactId: string;
}>;

export type SynergyProjectionGateRefInput = Readonly<{
  finalizationPackageId: string;
  finalizationPackageVersion: number;
  decisionLedgerDecisionId: string;
  orgScenarioId: string;
  toBeOrgStateId: string;
  activatedAt: string;
  activatedByUserId: string;
}>;

export type SynergyProjectionCurveSourceInput = Readonly<{
  sourceArtifactId: string;
  sourceRef?: string | null;
}>;

export type SynergyProjectionProposalMetadataInput = Readonly<{
  draftArtifactId: string;
  intentText: string;
  sourceCitation: Readonly<{
    page: number;
    section: string;
  }>;
  watchStreams?: readonly string[];
  approvedAt: string;
}>;

export type SynergyLeverCommitmentProjectionInput = Readonly<{
  commitment: SynergyLeverCommitment;
  approvedBand: SynergyProjectionApprovedBand;
  decisionId: string;
  metricBinding: SynergyProjectionMetricBindingInput;
  valueBinding: SynergyProjectionValueBindingInput;
  gateRef: SynergyProjectionGateRefInput;
  curveSource: SynergyProjectionCurveSourceInput;
  targetDate?: string;
  proposalMetadata?: SynergyProjectionProposalMetadataInput;
}>;

export type SynergyCommitmentBandGroupItem = Readonly<{
  synergyLeverId: string;
  conservativeRunRateUsd: number;
  baseRunRateUsd: number;
  stretchRunRateUsd: number;
}>;

export type SynergyCommitmentBandGroup = Readonly<{
  certaintyClass: SynergyCertaintyClass;
  commitments: readonly SynergyCommitmentBandGroupItem[];
}>;

export function validateSynergyLeverCommitment(
  value: unknown
): SynergyLeverCommitmentValidationResult {
  const errors: string[] = [];

  if (!isRecord(value)) {
    return { ok: false, errors: ['commitment must be an object'] };
  }

  validateEnum(
    value.schemaVersion,
    MA_SYNERGY_COMMITMENT_SCHEMA_VERSIONS,
    'schemaVersion',
    errors,
  );
  validateRequiredString(value.dealId, 'dealId', errors);
  validateRequiredString(value.scenarioVersionId, 'scenarioVersionId', errors);
  validateRequiredString(value.synergyLeverId, 'synergyLeverId', errors);
  validateRequiredString(value.title, 'title', errors);
  validateEnum(value.valueType, SYNERGY_VALUE_TYPES, 'valueType', errors);
  validateEnum(value.sourceMode, ['pre_deal', 'clean_room', 'day_1_plus'], 'sourceMode', errors);
  validateEnum(value.certaintyClass, SYNERGY_CERTAINTY_CLASSES, 'certaintyClass', errors);
  validateSynergyUnderwritingBands(
    value.bands,
    value.schemaVersion === MA_SYNERGY_COMMITMENT_V2_SCHEMA_VERSION &&
      value.customerOverlap !== undefined,
    errors,
  );
  validateOwner(value.owner, errors);
  validateStringArray(value.sizingEvidenceRefIds, 'sizingEvidenceRefIds', errors);
  validatePoolFootprint(value.poolFootprint, errors);
  validateStringArray(value.overlapDimensionKeys, 'overlapDimensionKeys', errors);
  validateRealizationEvidence(value.realizationEvidence, errors);
  validateRealizationCurve(value.realizationCurve, errors);
  validateStringArray(value.blockingGapCodes, 'blockingGapCodes', errors);
  validateNullableString(
    value.governedCommitmentDecisionId,
    'governedCommitmentDecisionId',
    errors
  );
  validateCustomerOverlapLeverExtension(
    value.customerOverlap,
    value.schemaVersion === MA_SYNERGY_COMMITMENT_V2_SCHEMA_VERSION,
    errors,
  );
  validateGovernedProjectionPrerequisites(value, errors);

  if (value.certaintyClass === 'blocked' && isEmptyStringArray(value.blockingGapCodes)) {
    errors.push('blocked commitments must carry at least one blockingGapCode');
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  return { ok: true, value: value as SynergyLeverCommitment };
}

export function projectSynergyLeverCommitmentToDecisionLedger(
  input: SynergyLeverCommitmentProjectionInput
): DecisionLedgerRealizationCommitmentDto {
  const validation = validateSynergyLeverCommitment(input.commitment);
  if (!validation.ok) {
    throw new Error(`Invalid SynergyLeverCommitment: ${validation.errors.join('; ')}`);
  }

  if (!isGovernedSynergyCertaintyClass(input.commitment.certaintyClass)) {
    throw new Error('Only c0_observed and c1_committed_lever synergies can project');
  }

  const targetDate = input.targetDate ?? input.commitment.realizationCurve.at(-1)?.asOf;
  if (!targetDate) {
    throw new Error('realizationCurve must include at least one point');
  }

  const proposalMetadata = input.proposalMetadata
    ? buildProposalMetadata(input.commitment, input.proposalMetadata)
    : undefined;

  return {
    schemaVersion: DECISION_LEDGER_REALIZATION_COMMITMENT_SCHEMA_VERSION,
    decisionId: input.decisionId,
    leverId: input.commitment.synergyLeverId,
    metricBinding: {
      metricKey: input.commitment.realizationEvidence.metricKey,
      label: input.metricBinding.label,
      unit: input.commitment.realizationEvidence.unit,
      direction: input.commitment.realizationEvidence.direction,
      definition: input.metricBinding.definition,
      scope: input.metricBinding.scope,
      source: input.commitment.realizationEvidence.observableSource,
      sourceArtifactId: input.metricBinding.sourceArtifactId,
      sourceMetricId: input.metricBinding.sourceMetricId,
      sensitivity: input.metricBinding.sensitivity,
      reconciliationRule: input.commitment.realizationEvidence.reconciliationRule,
      baselineAsOf: input.metricBinding.baselineAsOf,
      warningThresholdRatio: input.metricBinding.warningThresholdRatio ?? null,
      driftThresholdRatio: input.metricBinding.driftThresholdRatio ?? null,
      amendmentThresholdRatio: input.metricBinding.amendmentThresholdRatio ?? null,
    },
    baselineValue: input.metricBinding.baselineValue,
    targetValue: targetValueForBand(input.commitment.bands, input.approvedBand),
    targetDate,
    realizationCurve: input.commitment.realizationCurve.map((point) => ({
      asOf: point.asOf,
      plannedValue: point.plannedValue,
      sourceArtifactId: input.curveSource.sourceArtifactId,
      sourceRef: input.curveSource.sourceRef ?? null,
      timingProfile: point.timingProfile,
    })),
    valueBinding: {
      formulaId: input.valueBinding.formulaId,
      formulaVersion: input.valueBinding.formulaVersion,
      formulaRef: input.valueBinding.formulaRef,
      outputMetric: input.valueBinding.outputMetric,
      source: 'approved_formula_registry',
      asOf: input.valueBinding.asOf,
      sourceArtifactId: input.valueBinding.sourceArtifactId,
    },
    owner: {
      ownerUserId: input.commitment.owner.ownerUserId,
      ownerLabel: input.commitment.owner.ownerLabel,
      source: `ma_fusion_scenario_activation:${input.commitment.scenarioVersionId}:${input.commitment.owner.workstreamId}`,
    },
    propagationBinding: {
      packageInitiativeKey: input.commitment.synergyLeverId,
      source: 'ma_synergy_lever',
    },
    gateRef: {
      ...input.gateRef,
      receiptKind: 'ma_fusion_scenario_activation',
    },
    proposalMetadata,
  };
}

export function groupSynergyCommitmentBandsByCertaintyClass(
  commitments: readonly SynergyLeverCommitment[]
): readonly SynergyCommitmentBandGroup[] {
  const grouped = new Map<SynergyCertaintyClass, SynergyCommitmentBandGroup>();

  for (const commitment of commitments) {
    const validation = validateSynergyLeverCommitment(commitment);
    if (!validation.ok) {
      throw new Error(`Invalid SynergyLeverCommitment: ${validation.errors.join('; ')}`);
    }

    const current = grouped.get(commitment.certaintyClass);
    grouped.set(commitment.certaintyClass, {
      certaintyClass: commitment.certaintyClass,
      commitments: [
        ...(current?.commitments ?? []),
        {
          synergyLeverId: commitment.synergyLeverId,
          conservativeRunRateUsd: commitment.bands.conservativeRunRateUsd,
          baseRunRateUsd: commitment.bands.baseRunRateUsd,
          stretchRunRateUsd: commitment.bands.stretchRunRateUsd,
        },
      ],
    });
  }

  return SYNERGY_CERTAINTY_CLASSES.flatMap((certaintyClass) => {
    const summary = grouped.get(certaintyClass);
    return summary ? [summary] : [];
  });
}

export function isGovernedSynergyCertaintyClass(
  certaintyClass: SynergyCertaintyClass
): certaintyClass is GovernedSynergyCertaintyClass {
  return certaintyClass === 'c0_observed' || certaintyClass === 'c1_committed_lever';
}

export function isMaSynergyCommitmentSchemaVersion(
  value: unknown,
): value is SynergyCommitmentSchemaVersion {
  return (
    typeof value === 'string' &&
    MA_SYNERGY_COMMITMENT_SCHEMA_VERSIONS.includes(
      value as SynergyCommitmentSchemaVersion,
    )
  );
}

function buildProposalMetadata(
  commitment: SynergyLeverCommitment,
  metadata: SynergyProjectionProposalMetadataInput
): DecisionLedgerRealizationCommitmentProposalMetadataDto {
  const watchStreams =
    metadata.watchStreams && metadata.watchStreams.length > 0
      ? [...metadata.watchStreams]
      : [
          `${commitment.realizationEvidence.sourceContextKind}:${commitment.realizationEvidence.metricKey}`,
        ];

  return {
    source: 'ma_fusion_scenario_activation',
    draftArtifactId: metadata.draftArtifactId,
    intentText: metadata.intentText,
    sourceCitation: metadata.sourceCitation,
    watchStreams,
    watchMapStatus: 'proposal_pending_binding',
    approvedAt: metadata.approvedAt,
  };
}

function targetValueForBand(
  bands: SynergyUnderwritingBands,
  approvedBand: SynergyProjectionApprovedBand
): number {
  if (approvedBand === 'conservative') return bands.conservativeRunRateUsd;
  if (approvedBand === 'stretch') return bands.stretchRunRateUsd;
  return bands.baseRunRateUsd;
}

function validateGovernedProjectionPrerequisites(
  value: Record<string, unknown>,
  errors: string[]
): void {
  if (!isGovernedCertaintyValue(value.certaintyClass)) return;

  if (isEmptyStringArray(value.sizingEvidenceRefIds)) {
    errors.push('sizingEvidenceRefIds must include at least one evidence reference');
  }

  if (!hasAttributedPoolFootprintAtoms(value.poolFootprint)) {
    errors.push('governed synergies require an attributed poolFootprint');
  }

  if (isEmptyStringArray(value.overlapDimensionKeys)) {
    errors.push('governed synergies require overlapDimensionKeys for deconfliction');
  }

  if (Array.isArray(value.blockingGapCodes) && value.blockingGapCodes.length > 0) {
    errors.push('governed synergies cannot project while blockingGapCodes are present');
  }

  if (Array.isArray(value.realizationCurve) && value.realizationCurve.length < 2) {
    errors.push('governed synergies require at least two realizationCurve points');
  }
}

function validateOwner(value: unknown, errors: string[]): void {
  if (!isRecord(value)) {
    errors.push('owner must be an object');
    return;
  }

  validateNullableString(value.ownerUserId, 'owner.ownerUserId', errors);
  validateRequiredString(value.ownerLabel, 'owner.ownerLabel', errors);
  validateRequiredString(value.workstreamId, 'owner.workstreamId', errors);
}

function validateRealizationEvidence(value: unknown, errors: string[]): void {
  if (!isRecord(value)) {
    errors.push('realizationEvidence must be an object');
    return;
  }

  validateRequiredString(value.metricKey, 'realizationEvidence.metricKey', errors);
  validateRequiredString(value.unit, 'realizationEvidence.unit', errors);
  validateEnum(
    value.direction,
    ['increase', 'decrease', 'hold'],
    'realizationEvidence.direction',
    errors
  );
  validateRequiredString(value.observableSource, 'realizationEvidence.observableSource', errors);
  validateRequiredString(value.sourceContextKind, 'realizationEvidence.sourceContextKind', errors);
  validateRequiredString(
    value.reconciliationRule,
    'realizationEvidence.reconciliationRule',
    errors
  );
  validateRequiredString(value.proofArtifactKind, 'realizationEvidence.proofArtifactKind', errors);
}

function validateRealizationCurve(value: unknown, errors: string[]): void {
  if (!Array.isArray(value)) {
    errors.push('realizationCurve must be an array');
    return;
  }

  if (value.length === 0) {
    errors.push('realizationCurve must include at least one point');
  }

  value.forEach((point, index) => {
    if (!isRecord(point)) {
      errors.push(`realizationCurve[${index}] must be an object`);
      return;
    }
    validateRequiredString(point.asOf, `realizationCurve[${index}].asOf`, errors);
    validateFiniteNumber(point.plannedValue, `realizationCurve[${index}].plannedValue`, errors);
    validateRequiredString(point.timingProfile, `realizationCurve[${index}].timingProfile`, errors);
  });
}

function validatePoolFootprint(value: unknown, errors: string[]): void {
  if (!isRecord(value)) {
    errors.push('poolFootprint must be an object');
    return;
  }

  if (value.footprintStatus === 'attributed') {
    if (!Array.isArray(value.atoms) || value.atoms.length === 0) {
      errors.push('poolFootprint.atoms must include at least one atom when attributed');
      return;
    }

    value.atoms.forEach((atom, index) => {
      if (!isRecord(atom)) {
        errors.push(`poolFootprint.atoms[${index}] must be an object`);
        return;
      }
      validateEnum(
        atom.atomKind,
        ['cost_pool', 'position', 'account', 'lane', 'revenue_line'],
        `poolFootprint.atoms[${index}].atomKind`,
        errors
      );
      validateRequiredString(atom.atomId, `poolFootprint.atoms[${index}].atomId`, errors);
      validateRequiredString(atom.scope, `poolFootprint.atoms[${index}].scope`, errors);
      validateRequiredString(atom.timeWindow, `poolFootprint.atoms[${index}].timeWindow`, errors);
      validateFiniteNumber(atom.drawnUsd, `poolFootprint.atoms[${index}].drawnUsd`, errors);
    });
    return;
  }

  if (value.footprintStatus !== 'unattributed') {
    errors.push('poolFootprint.footprintStatus must be attributed or unattributed');
  }
}

function validateEnum<T extends readonly string[]>(
  value: unknown,
  allowed: T,
  path: string,
  errors: string[]
): void {
  if (typeof value !== 'string' || !allowed.includes(value)) {
    errors.push(`${path} is invalid`);
  }
}

function validateRequiredString(value: unknown, path: string, errors: string[]): void {
  if (typeof value !== 'string' || value.trim().length === 0) {
    errors.push(`${path} is required`);
  }
}

function validateNullableString(value: unknown, path: string, errors: string[]): void {
  if (!(typeof value === 'string' || value === null)) {
    errors.push(`${path} must be a string or null`);
  }
}

function validateFiniteNumber(value: unknown, path: string, errors: string[]): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    errors.push(`${path} must be a non-negative finite number`);
    return null;
  }
  return value;
}

function validateStringArray(value: unknown, path: string, errors: string[]): void {
  if (!Array.isArray(value)) {
    errors.push(`${path} must be an array`);
    return;
  }

  value.forEach((item, index) => {
    if (typeof item !== 'string' || item.trim().length === 0) {
      errors.push(`${path}[${index}] must be a non-empty string`);
    }
  });
}

function isEmptyStringArray(value: unknown): boolean {
  return Array.isArray(value) && value.length === 0;
}

function hasAttributedPoolFootprintAtoms(value: unknown): boolean {
  return isRecord(value) && value.footprintStatus === 'attributed' && Array.isArray(value.atoms)
    ? value.atoms.length > 0
    : false;
}

function isGovernedCertaintyValue(value: unknown): value is GovernedSynergyCertaintyClass {
  return value === 'c0_observed' || value === 'c1_committed_lever';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
